import {
    AccountProfilesRolesParams,
    Person,
    Profile,
    RoleQueryParams,
    UpdateProfileRolesParams,
    UserResponse,
    SelectMemberParams,
    SearchMemberParams,
    SearchMemberData,
    AccountProfilesRolesPaginatedResponse,
    AccountProfilesRolesPaginatedParams,
    CreateMembershipParams,
    MembershipRejoinData,
    EndMembershipParams,
    CreatePersonParams,
    MembershipProfileResponse,
    ResendVerificationEmailParams, CancelMemberBookingsParams, MigrateMemberToV7Return
} from '../../types/accountTypes';
import {apiSlice} from './apiSlice';
import {RootState} from '../store';
import {BaseResponse} from '../../types/apiTypes';

export const accountInjectedEndpoints = apiSlice.injectEndpoints({
    endpoints: builder => ({
        getPerson: builder.query<Person, number | undefined>({
            query: (id) => `/people/${id}`,
            providesTags: ['Person'],
        }),
        createPerson: builder.mutation<number, CreatePersonParams>({
            query: ({profile_id, first_name, last_name, birth_date,}) => ({
                url: `/people`,
                method: 'POST',
                body: {
                    profile_id,
                    first_name,
                    last_name,
                    birth_date,
                }
            }),
            invalidatesTags: ['Person'],
        }),
        updatePerson: builder.mutation<Person, Person>({
            query: ({id, first_name, last_name, birth_date, nickname}) => ({
                url: `/people/${id}`,
                method: 'PUT',
                body: {
                    first_name,
                    last_name,
                    birth_date,
                    nickname,
                }
            }),
            invalidatesTags: ['Person'],
        }),
        getUser: builder.query<UserResponse, void>({
            // Using ignore as queryFn appears to not like being called asynchronously, it can not settle on a return type.
            // This route is currently not in use, as components will just fetch the user in the store.
            // @ts-ignore
            queryFn: async (arg, api, extraOptions, baseQuery) => {
                const {user} = api.getState() as RootState;
                const userID = user.user?.id;
                if (!userID) {
                    return {
                        data: undefined,
                        error: {
                            data: {
                                message: "getUser Failed",
                            }
                        },
                    }
                }

                const response = await baseQuery({
                    url: `/users/${userID}`,
                });

                const data = response.data as BaseResponse;

                return {data};
            },
            providesTags: ['User'],
        }),
        getProfile: builder.query<Profile, number>({
            query: (id): string => `/profiles/${id}`,
            providesTags: (result, error, id) => [{type: 'Profile', id}],
        }),
        getProfilesAndRoles: builder.query<Profile[], void>({
            query: () => '/profiles/all',
            providesTags: result => result
                ? result.map(({id}) => ({type: 'Profile', id}))
                : ['Profile'],
        }),
        getAccountProfilesRoles: builder.query<Profile[], AccountProfilesRolesParams>({
            query: ({accountID}) => `/profiles?account_id=${accountID}&role_management=1&active_memberships=1`,
            providesTags: ['AccountProfile'],
        }),
        getAllAvailableRoles: builder.query<string[], RoleQueryParams>({
            query: ({roleType = '', roleSubType = ''}) => `/profiles/available_roles?role_type=${roleType}&role_sub_type=${roleSubType}`,
        }),
        getAccountProfilesRolesPaginated: builder.query<AccountProfilesRolesPaginatedResponse, AccountProfilesRolesPaginatedParams>({
            query: ({page = 1, perPage = 10, accountID, activeMemberships}) =>
                `/profiles?per_page=${perPage}&page=${page}&account_id=${accountID}&role_management=0&active_memberships=${activeMemberships}`,
            providesTags: ['AccountProfile'],
        }),
        updateProfileRoles: builder.mutation<Profile, UpdateProfileRolesParams>({
            query: ({profileID, roles, accountID}) => ({
                url: `/profiles/${profileID}/sync_roles`,
                method: 'POST',
                body: {roles, account_id: accountID},
            }),
            invalidatesTags: ['Profile', 'AccountProfile'],
        }),
        rejoinMembership: builder.mutation<void, CreateMembershipParams>({
            query: ({profileID, accountID, startedAt, endedAt}) => {
                const body: MembershipRejoinData = {
                    profile_id: profileID,
                    account_id: accountID,
                };

                if (startedAt) body['started_at'] = startedAt;
                if (endedAt) body['ended_at'] = endedAt;

                return {
                    url: `/memberships`,
                    method: 'POST',
                    body
                };
            },
            invalidatesTags: ['Profile', 'AccountProfile'],
        }),
        endMembership: builder.mutation<void, EndMembershipParams>({
            query: ({membershipID}) => ({
                url: `/memberships/${membershipID}/end_membership`,
                method: 'PUT'
            }),
            invalidatesTags: ['Profile', 'AccountProfile'],
        }),
        closeAccount: builder.mutation<void, number>({
            query: (id) => ({
                url: `/accounts/${id}/close`,
                method: 'POST'
            }),
            invalidatesTags: ['Jail', 'AccountProfile', 'Profile']
        }),
        migrateMemberToV7: builder.mutation<MigrateMemberToV7Return, number>({
            query: (id) => ({
                url: `/members/migrate`,
                method: 'POST',
                body: {profile_id: id}
            }),
            invalidatesTags: ['Jail', 'AccountProfile', 'Tickets'],
        }),
        getSelectMember: builder.query<Profile, SelectMemberParams>({
            query: ({account_membership_number}) => `/members/select?account_membership_number=${account_membership_number}`,
        }),
        getSearchMember: builder.query<SearchMemberData, SearchMemberParams>({
            query: ({
                        page = 1,
                        perPage = 10,
                        first_name = null,
                        last_name = null,
                        phone = null,
                        company = null,
                        email = null
                    }) => {
                let queryString = `/members/search?page=${page}&per_page=${perPage}`;

                if (first_name) {
                    first_name = encodeURIComponent(first_name);
                    queryString += `&first_name=${first_name}`;
                }

                if (last_name) {
                    last_name = encodeURIComponent(last_name);
                    queryString += `&last_name=${last_name}`;
                }

                if (phone) {
                    phone = encodeURIComponent(phone);
                    queryString += `&phone=${phone}`;
                }

                if (company) {
                    company = encodeURIComponent(company);
                    queryString += `&company=${company}`;
                }

                if (email) {
                    email = encodeURIComponent(email);
                    queryString += `&email=${email}`;
                }

                return queryString;
            }
        }),
        deleteMember: builder.mutation<{ message: string }, number>({
            query: (id) => ({
                url: `/members/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Profile', 'AccountProfile', 'Tickets'],
        }),
        getBookingMemberships: builder.query<MembershipProfileResponse[], number>({
            query: (id) => `memberships/get_booking_memberships?booking_id=${id}`,
            providesTags: ['Booking', 'AccountProfile'],
        }),
        cancelMemberBookings: builder.mutation<void, CancelMemberBookingsParams>({
            query: ({id, booking_started_at = null, booking_ended_at = null}) => ({
                url: `/memberships/${id}/bookings`,
                method: 'DELETE',
                body: {booking_started_at, booking_ended_at}
            }),
            invalidatesTags: ['Booking'],
        }),
        resendVerificationEmail: builder.mutation<void, ResendVerificationEmailParams>({
            query: ({user_id, is_staff}) => ({
                url: `/users/resend_verification_email`,
                method: 'POST',
                body: {
                    'id': user_id,
                    'is_staff': is_staff
                }
            }),
        })
    })
});

export const accountEndpoints = accountInjectedEndpoints.endpoints;
export const {
    useGetPersonQuery,
    useUpdatePersonMutation,
    useCreatePersonMutation,
    useLazyGetProfilesAndRolesQuery,
    useLazyGetProfileQuery,
    useGetAccountProfilesRolesQuery,
    useGetAllAvailableRolesQuery,
    useUpdateProfileRolesMutation,
    useGetAccountProfilesRolesPaginatedQuery,
    useRejoinMembershipMutation,
    useEndMembershipMutation,
    useCloseAccountMutation,
    useMigrateMemberToV7Mutation,
    useLazyGetSelectMemberQuery,
    useLazyGetSearchMemberQuery,
    useGetBookingMembershipsQuery,
    useDeleteMemberMutation,
    useCancelMemberBookingsMutation,
    useResendVerificationEmailMutation,
} = accountInjectedEndpoints;
